'use client'

import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/shared/ui/button/button'
import { CompanyCard } from '@/shared/ui/company-card/company-card'
import { CompanyModal } from '@/shared/ui/company-modal/company-modal'
import { Company } from '@/shared/types/company'
import { useCompaniesCount } from '@/shared/lib/hooks/useCompaniesCount'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
}

const item = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
}

export const CompaniesCatalog = () => {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const { count: totalCount } = useCompaniesCount()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Загрузка компаний из API
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true)
        // eslint-disable-next-line no-console
        console.log('Начинаю загрузку компаний для каталога...')

        // Небольшая задержка для лучшего UX
        await new Promise((resolve) => setTimeout(resolve, 300))

        // Используем carousel=true для получения рандомного порядка
        const response = await fetch('/api/companies?limit=12&carousel=true')
        if (response.ok) {
          const data = await response.json()
          // eslint-disable-next-line no-console
          console.log('Загружены компании для каталога:', data.companies?.length, data.companies)
          setCompanies(data.companies || [])
        } else {
          // eslint-disable-next-line no-console
          console.error('API ошибка:', response.status, response.statusText)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Ошибка загрузки компаний:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCompanies()
  }, []) // Убираем зависимость от companies.length, чтобы загружать каждый раз

  const handleCompanyClick = (company: Company) => {
    setSelectedCompany(company)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedCompany(null)
  }

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      // На мобилке скроллим на ширину одной карточки (320px + gap 24px)
      // На десктопе - на 400px как раньше
      const isMobile = window.innerWidth < 768
      const scrollAmount = isMobile ? 344 : 400

      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      // На мобилке скроллим на ширину одной карточки (320px + gap 24px)
      // На десктопе - на 400px как раньше
      const isMobile = window.innerWidth < 768
      const scrollAmount = isMobile ? 344 : 400

      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const handleTitleClick = () => {
    window.location.href = '/companies'
  }

  return (
    <>
      <div className="w-full bg-muted/50">
        <section className="container py-12 md:py-24">
          {/* Заголовок и навигация */}
          <div className="flex items-center justify-between mb-8 md:mb-12">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold cursor-pointer hover:text-orange-500 transition-colors"
              onClick={handleTitleClick}
            >
              В продаже{' '}
              {totalCount > 0 && (
                <>
                  <span className="text-orange-500 font-semibold">{totalCount}</span>
                  <span className="text-foreground"> компаний</span>
                </>
              )}
            </motion.h2>

            {/* Кнопки навигации */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={scrollLeft}
                className="h-10 w-10 rounded-full border-2 hover:border-orange-500 hover:text-orange-500"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={scrollRight}
                className="h-10 w-10 rounded-full border-2 hover:border-orange-500 hover:text-orange-500"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Карусель */}
          <motion.div
            variants={container}
            initial="hidden"
            animate={!loading ? 'show' : 'hidden'}
            className="relative"
          >
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide py-4"
              style={
                {
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                } as React.CSSProperties
              }
            >
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <motion.div
                    key={`skeleton-${index}`}
                    variants={item}
                    transition={{ duration: 0.5 }}
                    className="flex-none w-[320px] md:w-[380px]"
                  >
                    <div className="bg-card border rounded-lg p-6 animate-pulse">
                      <div className="h-6 bg-muted rounded mb-4"></div>
                      <div className="h-4 bg-muted rounded mb-2"></div>
                      <div className="h-4 bg-muted rounded mb-4 w-3/4"></div>
                      <div className="h-8 bg-muted rounded"></div>
                    </div>
                  </motion.div>
                ))
              ) : companies.length > 0 ? (
                // companies[0] - случайная компания
                // companies[1] - последняя загруженная компания (выделена)
                // companies[2+] - остальные случайные компании
                companies.map((company, index) => (
                  <motion.div
                    key={company.id}
                    variants={item}
                    transition={{
                      duration: 0.5,
                      delay: index === 1 ? 0 : index * 0.1 // Вторая компания (последняя загруженная) появляется быстрее
                    }}
                    className={`flex-none relative ${
                      'w-[320px] md:w-[380px]' // Все карточки одинакового размера
                    }`}
                  >
                    {/* Индикатор "Новинка" для второй компании */}
                    {index === 1 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                        className="absolute -top-2 -right-2 z-20 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
                      >
                        НОВИНКА
                      </motion.div>
                    )}
                    <CompanyCard
                      company={company}
                      onDetailsClick={handleCompanyClick}
                      className={
                        index === 1 ? 'ring-2 ring-orange-500 shadow-xl relative z-10' : ''
                      } // Выделение второй компании
                    />
                  </motion.div>
                ))
              ) : (
                <div className="w-full text-center py-8">
                  <p className="text-muted-foreground">Нет доступных компаний</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Кнопка "Показать все" */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-8"
          >
            <Button
              onClick={handleTitleClick}
              variant="outline"
              className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white px-8 py-2"
            >
              Посмотреть все компании
            </Button>
          </motion.div>
        </section>
      </div>

      {/* Модальное окно */}
      <CompanyModal company={selectedCompany} isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  )
}
